import { Component, inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  ValidatorFn,
} from '@angular/forms';
import { environment } from '../../../environments/environment';
import { BookingFieldConfig, BookingFieldOption, BookingFieldValidation } from '../../core/models/booking';
import { Emailer } from '../../core/services/emailer';
import { Email } from '../../core/models/email';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { Whatsapper } from '../../core/services/whatsapper';
import {DateTime} from 'luxon';
import { Helper } from '../../core/services/helper';

@Component({
  selector: 'app-booking',
  imports: [
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatRadioModule
  ],
  templateUrl: './booking.html',
  styleUrl: './booking.scss',
})
export class Booking {
  private readonly fb = inject(FormBuilder);
  private readonly emailer = inject(Emailer);
  private readonly whatsapper = inject(Whatsapper);
  private readonly helper = inject(Helper);

  booking: any = environment.BookingConfig;
  formColumns = 2;
  sortedFields = (this.booking.fields as BookingFieldConfig[])
    .slice()
    .sort(
      (a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY),
    ) as BookingFieldConfig[];
  fieldPositions = new Map<string, { columnStart: number; rowStart: number; span: number }>();
  bookingForm: FormGroup;

  constructor() {
    const occupiedPositions = new Set<number>();
    const fieldOrder = new Map<string, number>();

    this.sortedFields.forEach((field) => {
      if (field.order != null) {
        occupiedPositions.add(field.order);
        fieldOrder.set(field.name, field.order);
      }
    });

    let nextAvailable = 1;
    this.sortedFields.forEach((field) => {
      if (fieldOrder.has(field.name)) {
        return;
      }

      while (occupiedPositions.has(nextAvailable)) {
        nextAvailable += 1;
      }

      occupiedPositions.add(nextAvailable);
      fieldOrder.set(field.name, nextAvailable);
      nextAvailable += 1;
    });

    this.sortedFields.forEach((field) => {
      const position = fieldOrder.get(field.name) ?? 1;
      const columnStart = ((position - 1) % this.formColumns) + 1;
      const rowStart = Math.ceil(position / this.formColumns);
      this.fieldPositions.set(field.name, {
        columnStart,
        rowStart,
        span: Math.min(field.columns ?? 1, this.formColumns),
      });
    });

    this.bookingForm = this.fb.group(
      this.sortedFields.reduce(
        (controls, field) => {
          const validators = this.createValidators(field.validation);
          const emptyValue = field.multiple ? [] : '';
          controls[field.name] = [field.defaultValue ?? emptyValue, validators];
          return controls;
        },
        {} as { [key: string]: any },
      ),
    );

    this.sortedFields.forEach((field) => {
      if (!field.dependsOn) {
        return;
      }

      const parentControl = this.getFieldControl(field.dependsOn);
      const dependentControl = this.getFieldControl(field.name);
      const emptyValue = field.multiple ? [] : '';
      parentControl?.valueChanges.subscribe(() => dependentControl?.setValue(emptyValue));
    });
  }

  private getParentValues(field: BookingFieldConfig): string[] {
    const parentValue = this.getFieldControl(field.dependsOn ?? '')?.value;
    if (parentValue == null || parentValue === '') {
      return [];
    }

    return Array.isArray(parentValue) ? parentValue : [parentValue];
  }

  isFieldVisible(field: BookingFieldConfig): boolean {
    if (!field.dependsOn) {
      return true;
    }

    return this.getParentValues(field).some((value) => !!field.optionsByValue?.[value]?.length);
  }

  getFieldOptions(field: BookingFieldConfig): BookingFieldOption[] {
    if (!field.dependsOn) {
      return field.options ?? [];
    }

    const seen = new Set<string>();
    const options: BookingFieldOption[] = [];

    this.getParentValues(field).forEach((parentValue) => {
      (field.optionsByValue?.[parentValue] ?? []).forEach((option) => {
        if (!seen.has(option.value)) {
          seen.add(option.value);
          options.push(option);
        }
      });
    });

    return options;
  }

  getMinDate(moreThanField?: string) {
    //if no minimum, set minimum to today
    if (!moreThanField) {
      return new Date();
    }

    const moreThanControl = this.getFieldControl(moreThanField);
    const moreThan = moreThanControl ? moreThanControl.value : null;

    if (!moreThan) {
      return new Date();
    }

    return moreThan;
  }

  getMaxDate(lessThanField?: string) {
    if (!lessThanField) {
      return null;
    }
    const lessThanControl = this.getFieldControl(lessThanField);
    return lessThanControl ? lessThanControl.value : null;
  }

  getFieldGridStyles(field: BookingFieldConfig) {
    const position = this.fieldPositions.get(field.name);
    if (!position) {
      return {};
    }

    return {
      'grid-column': `${position.columnStart} / span ${position.span}`,
      'grid-row': `${position.rowStart}`,
    };
  }

  createValidators(validation?: BookingFieldValidation): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (!validation) {
      return validators;
    }

    if (validation.required) {
      validators.push(Validators.required);
    }
    if (validation.email) {
      validators.push(Validators.email);
    }
    if (validation.minLength != null) {
      validators.push(Validators.minLength(validation.minLength));
    }
    if (validation.maxLength != null) {
      validators.push(Validators.maxLength(validation.maxLength));
    }
    if (validation.pattern) {
      validators.push(Validators.pattern(validation.pattern));
    }

    return validators;
  }

  getFieldControl(name: string) {
    return this.bookingForm.get(name);
  }

  isFieldInvalid(name: string) {
    const control = this.getFieldControl(name);
    return !!control && control.invalid && control.touched;
  }

  hasError(field: BookingFieldConfig, error: string) {
    const control = this.getFieldControl(field.name);
    return !!control && control.hasError(error) && control.touched;
  }

  getErrorMessage(field: BookingFieldConfig, errorKey: string): string {
    const template = (this.booking.errorMessages as { [key: string]: string })?.[errorKey] ?? '';
    return this.helper.fillTemplatePlaceholders(template, {
      field: field.label.replace('*', '').trim(),
      min: field.validation?.minLength,
      max: field.validation?.maxLength,
    });
  }

  submitBooking() {
    if (this.bookingForm.invalid) {
      this.bookingForm.markAllAsTouched();
      return;
    }

    const value = this.bookingForm.value;

    if (environment.AppConfig.CommunicationPreference === 'email') {
      this.sendBookingEmail(value);
    } else if (environment.AppConfig.CommunicationPreference === 'whatsapp') {
      this.sendBookingWhatsapp(value);
    }

    this.bookingForm.reset();
  }

  sendBookingEmail(value: any) {
    const email: Email = {
      toAddress: environment.AppConfig.EmailService.SendTo,
      subject: this.helper.fillTemplatePlaceholders(this.booking.messageTemplates?.email.subject, value),
      body: this.helper.fillTemplatePlaceholders(this.booking.messageTemplates?.email.body, value),
    };

    this.emailer.sendEmail(email).subscribe({
      next: (response) => {
        console.log('Email sent successfully:', response);
      },
      error: (error) => {
        console.error('Error sending email:', error);
      },
    });
  }

  sendBookingWhatsapp(value : any){
    const template = this.booking.messageTemplates?.whatsapp;
    const message = this.helper.fillTemplatePlaceholders(template, value);
    this.whatsapper.sendWhatsApp(message);
  }

}
